import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "summary",
    title: "1. Executive Summary & Principles",
    tag: "Overview",
    content: (
      <div>
        <p>
          The <b>Insomniacs War Room</b> is an operational mission-control dashboard providing real-time visibility, cadence tracking, and accountability across all Insomniacs playgrounds.
        </p>
        <div className="manual-callout callout-info">
          <b>Core Operating Principles:</b>
          <ul>
            <li><b>One Glance, High Stakes:</b> The entire portfolio is monitored on a single dashboard to catch slipping momentum before it escalates.</li>
            <li><b>Health is an Algorithmic Clock:</b> Status is mathematically derived from elapsed days since the last logged review vs. agreed cadence.</li>
            <li><b>Shared Accountability:</b> Daily reviewers stamp their attendance on a shared 14-weekday audit board.</li>
            <li><b>Strict Google Authentication:</b> No credentials to manage; authorized solely via approved Google domains and emails.</li>
            <li><b>Safe Concurrent Drafting:</b> In-memory draft buffers prevent live socket collisions or accidental overwrites.</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "access",
    title: "2. Access, Sign-In & Security",
    tag: "Security",
    content: (
      <div>
        <p>The War Room enforces strict corporate identity verification through Google OAuth 2.0.</p>
        <div className="manual-grid-2">
          <div className="manual-card">
            <h4>Google Workspace Only</h4>
            <p>Users sign in with their corporate Google account (e.g., <code>@insomniacs.in</code>). There are no passwords to remember, reset, or leak.</p>
          </div>
          <div className="manual-card">
            <h4>Allowlist Rules</h4>
            <p>Access is managed under <b>Access</b>. Domain rules (e.g. <code>@insomniacs.in</code>) admit new team members instantly; external partners can be allowlisted individually.</p>
          </div>
        </div>
        <div className="manual-callout callout-warn" style={{ marginTop: "12px" }}>
          <b>User Roles:</b>
          <ul>
            <li><b>Member:</b> Can review playgrounds, add new playgrounds, acknowledge daily sign-off, update weekly tasks, KPIs, and log meetings.</li>
            <li><b>Administrator:</b> Full access plus user allowlisting, role management, database exports, imports, and system seeding.</li>
            <li><b>Daily Reviewer:</b> Members expected to sign off on the War Room board daily (visible on the top AckBar).</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: "dashboard",
    title: "3. The War Room Dashboard",
    tag: "UI Tour",
    content: (
      <div>
        <p>The main screen (<code>/</code>) is composed of four high-density operational panels:</p>
        <ol className="manual-steps">
          <li>
            <b>Daily Acknowledgement Bar:</b> Displays chips for all daily reviewers. Click <i>Acknowledge</i> to record your sign-off for the day. Turns green when all members have checked in.
          </li>
          <li>
            <b>Portfolio Summary Rail:</b> High-level KPI counters tracking total playgrounds, healthy count, at-risk warnings, critical alerts, and pending reviews.
          </li>
          <li>
            <b>Search & State Filters:</b> When 7+ playgrounds exist, easily filter by keyword, owner, or toggle <i>Healthy</i>, <i>At Risk</i>, and <i>Critical</i> states.
          </li>
          <li>
            <b>Playground Tiles Grid:</b> Visual cards showcasing each playground's health gauge, cadence clock, days elapsed, and pending task counts.
          </li>
        </ol>
      </div>
    ),
  },
  {
    id: "health",
    title: "4. Health as a Clock (Algorithmic Status)",
    tag: "Cadence",
    content: (
      <div>
        <p>Health is not subjective — it is a clock measuring elapsed days against the playground's cadence.</p>
        <div className="manual-table-wrap">
          <table className="manual-table">
            <thead>
              <tr>
                <th>Cadence</th>
                <th>Healthy (Green)</th>
                <th>At Risk (Yellow)</th>
                <th>Critical (Red)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><b>Weekly</b></td>
                <td>&lt; 7 days since review</td>
                <td>7 to 14 days</td>
                <td>14+ days overdue</td>
              </tr>
              <tr>
                <td><b>Monthly</b></td>
                <td>&lt; 30 days since review</td>
                <td>30 to 45 days</td>
                <td>45+ days overdue</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="manual-callout callout-bad" style={{ marginTop: "12px" }}>
          <b>Never Reviewed = Critical</b>
          <p>
            A playground with zero logged meetings automatically computes as <b>Critical</b> (drawn with a hatched meter). On a freshly seeded board, all 10 start as Critical by design — unreviewed operations are inherently at risk.
          </p>
        </div>
        <div className="manual-card" style={{ marginTop: "12px" }}>
          <h4>Manual Override</h4>
          <p>
            In exceptional scenarios, you can override automatic health in the playground drawer. Selecting <i>Use automatic</i> returns tracking to the algorithmic cadence clock.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: "playgrounds",
    title: "5. The 10 Insomniacs Playgrounds",
    tag: "Directory",
    content: (
      <div>
        <p>The standard seed registers 10 core organizational playgrounds with cross-functional relationships:</p>
        <div className="manual-table-wrap">
          <table className="manual-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Playground</th>
                <th>Owner</th>
                <th>Cadence</th>
                <th>Relationships & Feeds</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><b>p01</b></td><td><Link to="/playground/p01">Technology</Link></td><td>Govind Rai</td><td>Weekly</td><td>Receives AI LinkedIn output from Business Edge.</td></tr>
              <tr><td><b>p02</b></td><td><Link to="/playground/p02">Reventers</Link></td><td>Bhavya</td><td>Monthly</td><td>Shares 8 L2 cohorts with Blueprint. Feeds social media vertical.</td></tr>
              <tr><td><b>p03</b></td><td><Link to="/playground/p03">BRIKKIN</Link></td><td>Shubham</td><td>Weekly</td><td>Populated by Business Edge's AI LinkedIn automation.</td></tr>
              <tr><td><b>p04</b></td><td><Link to="/playground/p04">Blueprint</Link></td><td>Abhishek Singh</td><td>Weekly</td><td>Directly shares Reventers' 8 L2 cohort structure.</td></tr>
              <tr><td><b>p05</b></td><td><Link to="/playground/p05">Business Edge</Link></td><td>Govind Rai</td><td>Weekly</td><td>AI LinkedIn pages populate BRIKKIN and Tech.</td></tr>
              <tr><td><b>p06</b></td><td><Link to="/playground/p06">INSIDR</Link></td><td>Neha Rai</td><td>Weekly</td><td>Feeds Reventers social vertical; mentored quarterly.</td></tr>
              <tr><td><b>p07</b></td><td><Link to="/playground/p07">Beat</Link></td><td>Vrish Sachdeva</td><td>Weekly</td><td>Performance marketing & campaign operations.</td></tr>
              <tr><td><b>p08</b></td><td><Link to="/playground/p08">Operations</Link></td><td>Kanika</td><td>Weekly</td><td>9 internal construct cadences listed in reference table.</td></tr>
              <tr><td><b>p09</b></td><td><Link to="/playground/p09">HDFC Initiative</Link></td><td>—</td><td>Weekly</td><td>Strategic institutional framework.</td></tr>
              <tr><td><b>p10</b></td><td><Link to="/playground/p10">Stage Three</Link></td><td>—</td><td>Weekly</td><td>Incubation & strategic growth vertical.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: "drawer",
    title: "6. Working a Playground (The Detail Drawer)",
    tag: "Workflow",
    content: (
      <div>
        <p>Clicking any tile opens the slide-out detail drawer at <code>/playground/:id</code>.</p>
        <div className="manual-grid-2">
          <div className="manual-card">
            <h4>Log a Meeting Today</h4>
            <p>One-click action that updates the last review date to today and resets the cadence clock immediately.</p>
          </div>
          <div className="manual-card">
            <h4>Save & In-Memory Drafts</h4>
            <p>Edits remain queued in local memory until you press <b>Save</b>. If you press Escape or click away with unsaved work, a confirmation guard protects your edits.</p>
          </div>
          <div className="manual-card">
            <h4>Weekly Focus Tasks</h4>
            <p>Assign tasks, set due dates, and update statuses (<code>Todo</code>, <code>Doing</code>, <code>Done</code>). Tasks are tracked per ISO week.</p>
          </div>
          <div className="manual-card">
            <h4>Key Performance Indicators (KPIs)</h4>
            <p>Track metric targets vs. actuals. Click <i>Stamp Reviewed</i> to verify progress or trigger an inline escalation notice.</p>
          </div>
          <div className="manual-card">
            <h4>Process Flow Stages</h4>
            <p>Customize operational lifecycle stages and map the progress of cohorts and deliverables.</p>
          </div>
          <div className="manual-card">
            <h4>Reference Data Table</h4>
            <p>Add structured tables for operational constructs, URLs, contact lists, and internal SLAs.</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "accountability",
    title: "7. Daily Review & Accountability Protocol",
    tag: "Discipline",
    content: (
      <div>
        <p>The War Room is operated every day by team leadership through an established morning ritual:</p>
        <div className="manual-callout callout-info">
          <b>The 5-Minute Morning Routine:</b>
          <ol style={{ margin: "6px 0 0 18px", padding: 0 }}>
            <li><b>09:30 IST:</b> Open the War Room.</li>
            <li><b>Acknowledge:</b> Stamp your review chip on the top bar.</li>
            <li><b>Inspect Summary Rail:</b> Spot any playground with a yellow/red meter.</li>
            <li><b>Review Overdue Items:</b> Check <Link to="/accountability">Accountability</Link> for missed reviews or overdue KPIs.</li>
            <li><b>Log or Escalate:</b> If a sync occurred, log the meeting. If blocked, notify the playground owner.</li>
          </ol>
        </div>
        <p style={{ marginTop: "12px" }}>
          The <Link to="/accountability">Accountability Page</Link> plots a public 14-weekday matrix of sign-offs alongside all lapsed reviews.
        </p>
      </div>
    ),
  },
  {
    id: "communications",
    title: "8. Communications & Inline Notifications",
    tag: "Messaging",
    content: (
      <div>
        <p>Keep teams aligned without leaving the War Room:</p>
        <ul>
          <li><b>Send a Note (<code>/note</code>):</b> Compose targeted dispatches directly to any member on the Core Group list.</li>
          <li><b>Inline Notify:</b> Overdue tasks, lapsed cadences, and unreviewed KPIs feature a <b>Notify</b> button that auto-formats a targeted reminder and logs the timestamp.</li>
        </ul>
        <p className="hintline">Note: If SMTP email integration is not configured on the server, notification actions log internally without failing.</p>
      </div>
    ),
  },
  {
    id: "admin",
    title: "9. Administrator Controls (Access & Data)",
    tag: "Admin",
    content: (
      <div>
        <p>Administrators have exclusive access to two management pages:</p>
        <div className="manual-grid-2">
          <div className="manual-card">
            <h4>Access Management (<code>/access</code>)</h4>
            <ul>
              <li>Add organizational domain rules (e.g. <code>@insomniacs.in</code>).</li>
              <li>Grant single-address exceptions.</li>
              <li>Promote members to Admin or toggle Daily Reviewer flags.</li>
              <li>Block users (preserves history for audit accountability).</li>
              <li><i>Built-in safety guards:</i> Admins cannot demote or block themselves, nor delete the rule their own access rests on.</li>
            </ul>
          </div>
          <div className="manual-card">
            <h4>Data Management (<code>/data</code>)</h4>
            <ul>
              <li><b>Export JSON:</b> Download timestamped portfolio backup.</li>
              <li><b>Import JSON:</b> Restore backups or import v1 single-file artifacts.</li>
              <li><b>Reset Board:</b> Re-seed default 10 playgrounds with safety confirmation.</li>
            </ul>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "faq",
    title: "10. Troubleshooting & FAQ",
    tag: "Support",
    content: <FaqAccordion />,
  },
];

const FAQ_ITEMS = [
  {
    q: "Why are all 10 playgrounds marked \"Critical\" initially?",
    a: (
      <p>
        Health is calculated based on days since the last review. A newly created playground has no reviews logged, meaning it has never been reviewed. Once you click <i>Log a meeting today</i>, it will turn Healthy (Green).
      </p>
    ),
  },
  {
    q: "How often does the board refresh data?",
    a: (
      <p>
        The client polls the server every 30 seconds. If someone else makes an edit, it will be reflected within half a minute.
      </p>
    ),
  },
  {
    q: "How do I share a direct link to a playground?",
    a: (
      <p>
        Simply copy the URL from your address bar when the drawer is open (e.g. <code>/playground/p03</code>). Anyone with access will land directly in that playground.
      </p>
    ),
  },
  {
    q: "Can I switch between Dark and Light mode?",
    a: (
      <p>
        Yes! Click the theme toggle icon in the top right header. Your preference will be remembered across sessions.
      </p>
    ),
  },
];

function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = (idx) => {
    setOpenIndex((cur) => (cur === idx ? null : idx));
  };

  return (
    <div className="manual-faq">
      {FAQ_ITEMS.map((item, idx) => {
        const isOpen = openIndex === idx;
        return (
          <div
            key={idx}
            className={"manual-faq-item" + (isOpen ? " is-open" : "")}
          >
            <button
              type="button"
              className="faq-summary"
              onClick={() => toggle(idx)}
              aria-expanded={isOpen}
            >
              <span>{item.q}</span>
              <span className={"faq-chevron" + (isOpen ? " rot" : "")} aria-hidden="true">
                ▾
              </span>
            </button>
            {isOpen && <div className="faq-answer">{item.a}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function ManualPage() {
  const [filter, setFilter] = useState("");
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const isClickScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const filteredSections = useMemo(() => {
    if (!filter.trim()) return SECTIONS;
    const q = filter.toLowerCase();
    return SECTIONS.filter(
      (s) => s.title.toLowerCase().includes(q) || s.tag.toLowerCase().includes(q)
    );
  }, [filter]);

  // Initial mount: check URL hash
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const exists = SECTIONS.some((s) => s.id === id);
      if (exists) {
        setActiveSection(id);
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            const yOffset = -90;
            const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 120);
      }
    }
  }, []);

  // Scrollspy: update left sidebar as user scrolls through sections
  useEffect(() => {
    const handleScroll = () => {
      if (isClickScrollingRef.current) return;
      if (!filteredSections.length) return;

      const elements = filteredSections
        .map((s) => document.getElementById(s.id))
        .filter(Boolean);

      if (!elements.length) return;

      // If scrolled to the bottom of the page, highlight the last section
      const isBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 60;

      if (isBottom) {
        setActiveSection(elements[elements.length - 1].id);
        return;
      }

      // Reading threshold: 150px below the top of viewport
      const threshold = window.scrollY + 150;
      let currentId = elements[0].id;

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];
        if (el.offsetTop <= threshold) {
          currentId = el.id;
        } else {
          break;
        }
      }

      setActiveSection(currentId);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [filteredSections]);

  // Keep active link visible in TOC list if it overflows
  useEffect(() => {
    const activeLink = document.querySelector(`.manual-toc-link[data-id="${activeSection}"]`);
    if (activeLink) {
      activeLink.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeSection]);

  // Smooth scroll when clicking a sidebar TOC link
  function scrollToSection(e, id) {
    e.preventDefault();
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      isClickScrollingRef.current = true;
      const yOffset = -90;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      history.replaceState(null, "", `#${id}`);

      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isClickScrollingRef.current = false;
      }, 700);
    }
  }

  function downloadManualMd() {
    fetch("/MANUAL.md")
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .catch(() => {
        return `# Insomniacs War Room — User & Operational Manual\n\nPlease refer to MANUAL.md in the repository root.`;
      })
      .then((text) => {
        const blob = new Blob([text], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "MANUAL.md";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  }

  return (
    <div className="page manual-page">
      <div className="pagehead">
        <div>
          <h2 className="pagetitle">User & Operational Manual</h2>
          <p className="pagesub">
            Complete guide to the Insomniacs War Room: health calculations, playgrounds, daily loops, and administration.
          </p>
        </div>
        <div className="pageacts">
          <button className="btn btn-sm" type="button" onClick={downloadManualMd} title="Download the raw MANUAL.md file">
            Download MANUAL.md
          </button>
          <Link className="btn btn-sm btn-solid" to="/">
            Back to Board
          </Link>
        </div>
      </div>

      <div className="manual-layout">
        {/* Table of contents sidebar */}
        <aside className="manual-sidebar">
          <div className="manual-search-box">
            <input
              type="search"
              className="in in-sm manual-search-in"
              placeholder="Search guide & topics…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <nav className="manual-toc">
            {filteredSections.map((sec) => (
              <a
                key={sec.id}
                href={"#" + sec.id}
                data-id={sec.id}
                className={"manual-toc-link" + (activeSection === sec.id ? " active" : "")}
                onClick={(e) => scrollToSection(e, sec.id)}
              >
                <span className="toc-tag">{sec.tag}</span>
                <span className="toc-title">{sec.title}</span>
              </a>
            ))}
          </nav>
        </aside>

        {/* Main Content Sections */}
        <main className="manual-body">
          {filteredSections.map((sec) => (
            <article
              key={sec.id}
              id={sec.id}
              className={"manual-section" + (activeSection === sec.id ? " is-active" : "")}
            >
              <header className="manual-section-head">
                <span className="label">{sec.tag}</span>
                <h3 className="manual-section-title">{sec.title}</h3>
              </header>
              <div className="manual-section-content">{sec.content}</div>
            </article>
          ))}

          {filteredSections.length === 0 && (
            <div className="notice">
              <b>No matching topics found.</b> Try searching for <i>health</i>, <i>cadence</i>, <i>admin</i>, or clear the search query.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
